/**
 * Screening auto-population hook (PAC-017).
 *
 * Wraps the Cases service screening surface:
 *   - GET  /api/v1/cases/{id}/screening  → ScreeningForm
 *   - POST /api/v1/cases/{id}/screening  → ScreeningForm (confirm/correct)
 *
 * The screening template is pre-populated from prior data (census / EHR /
 * patient-reported). Each field carries its `source` and a `confirmed` flag so
 * the case page can render changed-field highlighting and a confirm/correct UX.
 *
 * Degrades gracefully: if the endpoint is not yet implemented (404) the hook
 * exposes `available = false` and an empty form so the page shows a friendly
 * disabled state rather than crashing.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';

/**
 * One pre-populated screening field with provenance + confirm state.
 * Mirrors `services/cases` `ScreeningField` (value is nullable on the wire).
 */
export interface ScreeningField {
  key: string;
  label: string;
  /** Current value (may be a correction of the auto-populated source value). */
  value: string | null;
  /** Provenance, e.g. `census`, `enrichment`. */
  source: string;
  /** Whether a human has confirmed (or corrected + confirmed) this field. */
  confirmed: boolean;
}

/** The screening prefill payload (`ScreeningPrefill` on the wire). */
export interface ScreeningForm {
  fields: ScreeningField[];
}

/** One confirmation/correction sent back to persist screening (PAC-017). */
export interface ScreeningConfirmation {
  key: string;
  value: string | null;
  confirmed: boolean;
}

interface UseScreeningResult {
  fields: ScreeningField[];
  /** False when the endpoint 404s (surface a disabled empty state). */
  available: boolean;
  isLoading: boolean;
  isWorking: boolean;
  error: ApiError | null;
  actionError: ApiError | null;
  refresh: () => void;
  /** Persist confirm/correct edits → POST /cases/{id}/screening. */
  save: (fields: ScreeningField[]) => Promise<boolean>;
}

/** Loads the prefilled screening form and exposes the confirm/correct save. */
export function useScreening(caseId: string | undefined): UseScreeningResult {
  const [fields, setFields] = useState<ScreeningField[]>([]);
  const [available, setAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [actionError, setActionError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    if (!caseId) return;
    setIsLoading(true);
    setError(null);
    apiClient
      .get<ScreeningForm>(`/cases/${encodeURIComponent(caseId)}/screening`)
      .then((form) => {
        setFields(form.fields ?? []);
        setAvailable(true);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          // Endpoint not implemented yet — degrade to a friendly empty state.
          setAvailable(false);
          setFields([]);
          setError(null);
          return;
        }
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setFields([]);
      })
      .finally(() => setIsLoading(false));
  }, [caseId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const save = useCallback(
    async (next: ScreeningField[]): Promise<boolean> => {
      if (!caseId) return false;
      setIsWorking(true);
      setActionError(null);
      try {
        // The contract persists per-field confirmations/corrections.
        const confirmations: ScreeningConfirmation[] = next.map((f) => ({
          key: f.key,
          value: f.value,
          confirmed: f.confirmed,
        }));
        const form = await apiClient.post<ScreeningForm>(
          `/cases/${encodeURIComponent(caseId)}/screening`,
          { confirmations }
        );
        setFields(form?.fields ?? next);
        return true;
      } catch (err: unknown) {
        setActionError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return false;
      } finally {
        setIsWorking(false);
      }
    },
    [caseId]
  );

  return { fields, available, isLoading, isWorking, error, actionError, refresh, save };
}
